/**
 * City map tiles kept on the phone. A city's tile file (PMTiles, one file per city) is downloaded once, stored in the
 * browser's Cache Storage and read from there for every map view, so a phone costs us one read per data version, not one per
 * tile. Falls back to the network-only prototype tiles (the caller keeps those) when anything here fails.
 */
import { PMTiles, type RangeResponse, type Source } from 'pmtiles';
import { API } from '../lib/api';

const STORE = 'konkride-city-tiles-v1';
const loaded = new Map<string, Promise<PMTiles | null>>();

export const cityTileUrl = (city: string, version: string): string => `${API}/maps/tiles/${city}.pmtiles?v=${encodeURIComponent(version)}`;

class BlobSource implements Source {
  constructor(private key: string, private blob: Blob) {}
  getKey(): string { return this.key; }
  async getBytes(offset: number, length: number): Promise<RangeResponse> {
    return { data: await this.blob.slice(offset, offset + length).arrayBuffer() };
  }
}

/** Ask the browser to keep our storage when space runs low. Best effort: some browsers say no. */
async function keepStorage(): Promise<void> {
  try { await navigator.storage?.persist?.(); } catch { /* not supported */ }
}

async function fetchBlob(url: string): Promise<Blob> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`tiles ${r.status}`);
  return r.blob();
}

/** The stored tile file for a city and data version. Downloads it on first use. Resolves null if it can't be had. */
export function loadCityTiles(city: string, version: string): Promise<PMTiles | null> {
  const url = cityTileUrl(city, version);
  let p = loaded.get(url);
  if (!p) {
    p = (async () => {
      try {
        let blob: Blob;
        if (typeof caches === 'undefined') blob = await fetchBlob(url); // no Cache Storage: keep it in memory for this visit only
        else {
          const store = await caches.open(STORE);
          const hit = await store.match(url);
          if (hit) blob = await hit.blob();
          else {
            const r = await fetch(url);
            if (!r.ok) throw new Error(`tiles ${r.status}`);
            await store.put(url, r.clone());
            blob = await r.blob();
            void keepStorage();
            // drop older versions of this city's file
            for (const k of await store.keys()) if (k.url !== url && k.url.includes(`/maps/tiles/${city}.pmtiles`)) await store.delete(k);
          }
        }
        return new PMTiles(new BlobSource(url, blob));
      } catch {
        loaded.delete(url); // try again next time
        return null;
      }
    })();
    loaded.set(url, p);
  }
  return p;
}
